'use client'

import React, { useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import {
  Mail,
  KeyRound,
  ArrowRight,
  CheckCircle2,
  AlertCircle,
  Building2,
} from 'lucide-react'

export interface LoginClientProps {
  initialTab?: 'email' | 'code'
  returnTo?: string
  clientIp?: string
  ipBypassedForTesting?: boolean
  initialError?: string
  compact?: boolean
}

export function LoginClient({
  initialTab = 'email',
  returnTo,
  initialError,
  compact = false,
}: LoginClientProps) {
  const router = useRouter()
  const [activeTab, setActiveTab] = useState<'email' | 'code'>(initialTab)

  // Email Magic Link / OTP state
  const [email, setEmail] = useState('')
  const [codeSent, setCodeSent] = useState(false)
  const [otpCode, setOtpCode] = useState('')
  const [matchedOrgName, setMatchedOrgName] = useState<string | null>(null)

  // Campus Access Code state
  const [accessCode, setAccessCode] = useState('')

  // Shared UI state
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(initialError || null)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)

  async function handleRequestEmailCode(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setSuccessMsg(null)
    setLoading(true)
    try {
      const res = await fetch('/api/wlib-auth/request-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })
      const data = await res.json()
      if (!res.ok || data.error) {
        setError(data.error || 'Failed to send login code.')
      } else {
        setCodeSent(true)
        setMatchedOrgName(data.matchedOrganization || null)
        setSuccessMsg(`Login code sent to ${data.email}.`)
      }
    } catch (err: any) {
      setError(err?.message || 'Network error while requesting code.')
    } finally {
      setLoading(false)
    }
  }

  async function handleVerifyEmailCode(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const res = await fetch('/api/wlib-auth/verify-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code: otpCode }),
      })
      const data = await res.json()
      if (!res.ok || data.error) {
        setError(data.error || 'Invalid verification code.')
      } else {
        setSuccessMsg('Logged in! Redirecting...')
        const target = returnTo || data.redirectTo || '/library'
        router.push(target)
        router.refresh()
      }
    } catch (err: any) {
      setError(err?.message || 'Verification failed.')
    } finally {
      setLoading(false)
    }
  }

  async function handleVerifyAccessCode(e: React.FormEvent, overrideCode?: string) {
    if (e) e.preventDefault()
    const codeToUse = (overrideCode ?? accessCode).trim()
    if (!codeToUse) {
      setError('Please enter an access code.')
      return
    }
    setError(null)
    setSuccessMsg(null)
    setLoading(true)
    try {
      const res = await fetch('/api/wlib-auth/verify-access-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accessCode: codeToUse }),
      })
      const data = await res.json()
      if (!res.ok || data.error) {
        setError(data.error || 'Unrecognized access code.')
      } else {
        setSuccessMsg(`Authenticated with ${data.organizationName}! Redirecting...`)
        const target = returnTo || data.redirectTo || '/library'
        router.push(target)
        router.refresh()
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to validate access code.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      className={`wl-login-box ${compact ? 'compact' : ''}`}
      style={{
        background: '#ffffff',
        color: '#1f1f1f',
        border: '1px solid #c4c4c8',
        borderRadius: 6,
        padding: compact ? '1.15rem' : '1.65rem',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
      }}
    >
      {error && (
        <div className="wl-alert wl-alert-error">
          <AlertCircle size={18} style={{ flexShrink: 0, marginTop: 2 }} />
          <div>{error}</div>
        </div>
      )}

      {successMsg && (
        <div className="wl-alert wl-alert-success">
          <CheckCircle2 size={18} style={{ flexShrink: 0, marginTop: 2 }} />
          <div>{successMsg}</div>
        </div>
      )}

      {activeTab === 'email' ? (
        <div>
          {!codeSent ? (
            <form onSubmit={handleRequestEmailCode}>
              <div className="wl-field">
                <label
                  className="wl-label"
                  htmlFor="wl-login-email"
                  style={{ color: '#27272a', fontWeight: 600 }}
                >
                  Email Address
                </label>
                <input
                  id="wl-login-email"
                  type="email"
                  required
                  className="wl-input"
                  style={{ fontSize: '16px', background: '#ffffff', color: '#1f1f1f' }}
                  placeholder="you@example.com or name@school.edu"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="wl-btn wl-btn-primary wl-btn-lg"
                style={{ width: '100%' }}
              >
                <span>{loading ? 'Sending Code...' : 'Sign In'}</span>
                <ArrowRight size={16} />
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyEmailCode}>
              {matchedOrgName && (
                <div className="wl-alert wl-alert-success">
                  <Building2 size={18} style={{ flexShrink: 0, marginTop: 2 }} />
                  <div>
                    Institution recognized: <strong>{matchedOrgName}</strong>
                  </div>
                </div>
              )}

              <div className="wl-field">
                <label
                  className="wl-label"
                  htmlFor="wl-otp-input"
                  style={{ color: '#27272a', fontWeight: 600 }}
                >
                  6-Digit Code (sent to {email})
                </label>
                <input
                  id="wl-otp-input"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  required
                  className="wl-input"
                  style={{
                    fontSize: '1.35rem',
                    letterSpacing: '0.3em',
                    textAlign: 'center',
                    fontFamily: 'var(--font-mono)',
                    fontWeight: 700,
                    background: '#ffffff',
                    color: '#1f1f1f',
                  }}
                  placeholder="000000"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value)}
                />
              </div>

              <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                <button
                  type="submit"
                  disabled={loading}
                  className="wl-btn wl-btn-primary wl-btn-lg"
                  style={{ flex: 1 }}
                >
                  <span>{loading ? 'Verifying...' : 'Verify & Sign In'}</span>
                  <ArrowRight size={16} />
                </button>
                <button
                  type="button"
                  className="wl-btn wl-btn-outline"
                  onClick={() => {
                    setCodeSent(false)
                    setOtpCode('')
                    setSuccessMsg(null)
                  }}
                >
                  Back
                </button>
              </div>
            </form>
          )}
        </div>
      ) : (
        <form onSubmit={(e) => handleVerifyAccessCode(e)}>
          <div className="wl-field">
            <label
              className="wl-label"
              htmlFor="wl-access-code"
              style={{ color: '#27272a', fontWeight: 600 }}
            >
              Library Access Code
            </label>
            <input
              id="wl-access-code"
              type="text"
              required
              className="wl-input"
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: '16px',
                fontWeight: 600,
                letterSpacing: '0.04em',
                background: '#ffffff',
                color: '#1f1f1f',
              }}
              placeholder="Enter Library Access Code"
              value={accessCode}
              onChange={(e) => setAccessCode(e.target.value)}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="wl-btn wl-btn-primary wl-btn-lg"
            style={{ width: '100%' }}
          >
            <span>{loading ? 'Checking Code...' : 'Sign In with Access Code'}</span>
            <ArrowRight size={16} />
          </button>
        </form>
      )}

      <div
        className="wl-login-footer-row"
        style={{
          marginTop: '1.1rem',
          paddingTop: '0.95rem',
          borderTop: '1px solid #e4e4e7',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '0.75rem',
        }}
      >
        {activeTab === 'email' ? (
          <button
            type="button"
            className="wl-btn wl-btn-outline wl-btn-sm"
            style={{
              padding: '0.42rem 0.8rem',
              fontSize: '0.82rem',
              fontWeight: 600,
              color: '#482c70',
              borderColor: '#d2bff0',
              background: '#f8f7fb',
            }}
            onClick={() => {
              setActiveTab('code')
              setError(null)
              setSuccessMsg(null)
            }}
          >
            <KeyRound size={14} />
            <span>Library Access Code</span>
          </button>
        ) : (
          <button
            type="button"
            className="wl-btn wl-btn-outline wl-btn-sm"
            style={{
              padding: '0.42rem 0.8rem',
              fontSize: '0.82rem',
              fontWeight: 600,
              color: '#482c70',
              borderColor: '#d2bff0',
              background: '#f8f7fb',
            }}
            onClick={() => {
              setActiveTab('email')
              setError(null)
              setSuccessMsg(null)
            }}
          >
            <Mail size={14} />
            <span>Email Sign In</span>
          </button>
        )}

        <Link
          href="/subscribe"
          style={{
            fontWeight: 700,
            fontSize: '0.88rem',
            color: '#482c70',
            textDecoration: 'underline',
            textUnderlineOffset: '3px',
            padding: '0.25rem 0',
          }}
        >
          Sign Up →
        </Link>
      </div>
    </div>
  )
}
